// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

export const KIOSK_FLAG = 'qh_kiosk'
export const KIOSK_VOICE_FLAG = 'qh_kiosk_voice'
export const KIOSK_RETURN_URL = 'qh_kiosk_return'

let navigationLocked = false
let lockedUrl = ''

export function isKioskSession(): boolean {
  return sessionStorage.getItem(KIOSK_FLAG) === '1'
}

export function setKioskSession(): void {
  sessionStorage.setItem(KIOSK_FLAG, '1')
}

export function clearKioskSession(): void {
  sessionStorage.removeItem(KIOSK_FLAG)
  sessionStorage.removeItem(KIOSK_RETURN_URL)
}

// Where the provider tablet should land after staff unlocks the kiosk — the
// provider-admin page that launched this session (a different origin). Passed in
// via ?return= at kiosk entry and stashed for the whole session. Only same-scheme
// http(s) absolute URLs are accepted (open-redirect guard).
export function setKioskReturnUrl(url: string): void {
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      sessionStorage.setItem(KIOSK_RETURN_URL, parsed.toString())
    }
  } catch {
    // not a valid absolute URL — ignore
  }
}

export function getKioskReturnUrl(): string | null {
  return sessionStorage.getItem(KIOSK_RETURN_URL)
}

export function returnToUrl(url: string): void {
  window.location.replace(url)
}

export function setKioskVoiceMode(): void {
  localStorage.setItem(KIOSK_VOICE_FLAG, '1')
}

export function consumeKioskVoiceMode(): boolean {
  const active = localStorage.getItem(KIOSK_VOICE_FLAG) === '1'
  if (active) localStorage.removeItem(KIOSK_VOICE_FLAG)
  return active
}

function kioskKeyHandler(e: KeyboardEvent) {
  // Block Esc (fullscreen exit), F11 (fullscreen toggle), Cmd/Ctrl+W (close tab)
  if (
    e.key === 'Escape' ||
    e.key === 'F11' ||
    ((e.metaKey || e.ctrlKey) && (e.key === 'w' || e.key === 'W')) ||
    (e.altKey && e.key === 'ArrowLeft') ||
    (e.metaKey && e.key === '[')
  ) {
    e.preventDefault()
    e.stopPropagation()
  }
}

function kioskPopStateHandler() {
  window.history.pushState({ qhKioskGuard: true }, '', lockedUrl || window.location.href)
}

function kioskBeforeUnloadHandler(e: BeforeUnloadEvent) {
  e.preventDefault()
}

export function lockKioskNavigation(): void {
  document.addEventListener('keydown', kioskKeyHandler, true)
  window.addEventListener('beforeunload', kioskBeforeUnloadHandler)
  if (!navigationLocked) {
    lockedUrl = window.location.href
    window.history.pushState({ qhKioskGuard: true }, '', lockedUrl)
    window.addEventListener('popstate', kioskPopStateHandler)
    navigationLocked = true
  }
}

export async function enterKioskMode(): Promise<boolean> {
  lockKioskNavigation()
  let fullscreenActive = Boolean(document.fullscreenElement)
  try {
    if (!fullscreenActive) {
      await document.documentElement.requestFullscreen({ navigationUI: 'hide' })
      fullscreenActive = true
    }
  } catch {
    fullscreenActive = false
  }
  return fullscreenActive
}

export function exitKioskMode(): void {
  if (document.fullscreenElement) {
    document.exitFullscreen().catch(() => {})
  }
  document.removeEventListener('keydown', kioskKeyHandler, true)
  window.removeEventListener('beforeunload', kioskBeforeUnloadHandler)
  window.removeEventListener('popstate', kioskPopStateHandler)
  navigationLocked = false
  lockedUrl = ''
}
